import axios from "axios";
import store from "./store";
import {logoutUser, updateToken} from "../reducers/User";

const baseURL = import.meta.env.VITE_API_BASE_URL;

/* ================= MAIN API INSTANCE ================= */
const api = axios.create({
    baseURL,
    timeout: 10000,
});

/* ================= FLAGS & QUEUE (Concurrency Fix) ================= */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/* ================= REQUEST INTERCEPTOR ================= */
api.interceptors.request.use(
    (config) => {
        // Prioritize Redux state, fallback to LocalStorage
        const token = store.getState().user.token || localStorage.getItem("token");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/* ================= RESPONSE INTERCEPTOR ================= */
api.interceptors.response.use(
    (response) => {
        return response.data; // Return only data on success
    },
    async (error) => {
        const originalRequest = error.config;

        // Check if error is 401 and avoid infinite loops
        if (error.response?.status === 401 && !originalRequest._retry) {

            // 1. If we are already refreshing, queue this request
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({resolve, reject});
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            // 2. Start Refresh Process
            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Rename variable to avoid shadowing function name
                const {refreshToken: currentRefreshToken} = store.getState().user;

                // Use a clean axios call to avoid interceptors
                const response = await axios.post(`${baseURL}/auth/admin/refresh`, {
                    refreshToken: currentRefreshToken
                });

                const newAccessToken = response.data?.accessToken;

                // Update Store & LocalStorage
                store.dispatch(updateToken(newAccessToken));

                // Update Authorization header for the failed request
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                // Process the queue of other failed requests
                processQueue(null, newAccessToken);

                // Return the result of the retry
                return api(originalRequest);

            } catch (err) {
                processQueue(err, null);
                store.dispatch(logoutUser());
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

/* ================= BASE METHODS ================= */
// Improved typing and default parameters
export const post = async ({path, reqBody = {}, header = {} , config = {}}) => {
    return api.post(path, reqBody, {headers: header , ...config});
};

export const get = async ({path, header = {}}) => {
    return api.get(path, {headers: header});
};

export default api;